/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
        Module No. 17, SBC, Thejaswini Building
        Phase 1, Technopark, Thiruvananthapuram - 695581, 
        Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import {
    FileText,
    Download,
    Plus,
    Filter,
    Search,
    Calendar,
    ArrowRight,
    Eye,
    AlertTriangle,
    CheckCircle2,
    Clock,
    FlaskConical,
    Beaker,
    MoreVertical,
    ChevronDown,
    Scan,
    Maximize
} from 'lucide-react';
import HeaderSection from '../../common/headerSection';
import PageHeader from '../../common/pageHeader';
import CustomToast from '../../../widgets/toaster';
import { DEFAULT_PAGE_SIZE } from '../../../constants/constants';
import Pagination from '../../../widgets/pagination';
import { SterilityTestServices } from '../../../service/sterilityTestServices';
import { helperFunctions } from '../../../utils/helper';
import SterilityObservationRecording from './Sterilityobservationrecording';
import SterilityObservationDetails from './Sterilityobservationdetails';
import { userService } from '../../../service/userService';

interface Observation {
    id: string;
    testId: string;
    productionSchedule: string;
    productName: string;
    productBatch: string;
    testMethod: string;
    tubeCount: number;
    observationDay: string;
    plannedDate: string;
    actualDate: string;
    result: 'Pass' | 'Fail' | 'Growth Detected' | '-';
    status: 'Pending' | 'Completed' | 'Overdue';
    recordedBy: string;
}

const MOCK_OBSERVATIONS: Observation[] = [
    {
        id: 'OBS-001',
        testId: 'ST-2024-X45',
        productionSchedule: 'SCH-2024-001',
        productName: 'Insulin Glargine',
        productBatch: 'B240501',
        testMethod: 'Membrane Filtration',
        tubeCount: 4,
        observationDay: 'Day 3',
        plannedDate: '15-Aug-2024',
        actualDate: '15-Aug 09:30',
        result: 'Pass',
        status: 'Completed',
        recordedBy: 'P Shyam Kumar'
    },
    {
        id: 'OBS-002',
        testId: 'ST-2024-X45',
        productionSchedule: 'SCH-2024-001',
        productName: 'Insulin Glargine',
        productBatch: 'B240501',
        testMethod: 'Membrane Filtration',
        tubeCount: 4,
        observationDay: 'Day 3',
        plannedDate: '15-Aug-2024',
        actualDate: '15-Aug 09:35',
        result: 'Pass',
        status: 'Completed',
        recordedBy: 'P Shyam Kumar'
    },
    {
        id: 'OBS-003',
        testId: 'ST-2024-Z22',
        productionSchedule: 'SCH-2024-012',
        productName: 'WFI 100ml',
        productBatch: 'W240812',
        testMethod: 'Direct Inoculation',
        tubeCount: 2,
        observationDay: 'Day 7',
        plannedDate: '02-Jun-2026',
        actualDate: '-',
        result: '-',
        status: 'Overdue',
        recordedBy: '-'
    },
    {
        id: 'OBS-004',
        testId: 'ST-2024-A10',
        productionSchedule: 'SCH-2024-005',
        productName: 'Oxytocin Inj',
        productBatch: 'O240610',
        testMethod: 'Membrane Filtration',
        tubeCount: 6,
        observationDay: 'Day 1',
        plannedDate: '02-Jun-2026',
        actualDate: '-',
        result: '-',
        status: 'Pending',
        recordedBy: '-'
    },
    {
        id: 'OBS-005',
        testId: 'ST-2024-B88',
        productionSchedule: 'SCH-2024-088',
        productName: 'Heparin Sodium',
        productBatch: 'H240115',
        testMethod: 'Membrane Filtration',
        tubeCount: 4,
        observationDay: 'Day 14',
        plannedDate: '02-Jun-2026',
        actualDate: '02-Jun 10:15',
        result: 'Growth Detected',
        status: 'Completed',
        recordedBy: 'Harish S'
    }
];

interface SterilityObservationProps {
    // onRecord?: () => void;
    // onViewDetails?: () => void;
}

const SterilityObservation: React.FC<SterilityObservationProps> = () => {

    const now = Date.now();
    const { t } = useTranslation();
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [activeQuickFilter, setActiveQuickFilter] = useState('All');
    // pagination
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sort, setSort] = useState("scheduled_date_time");
    const [order, setOrder] = useState("asc");
    const [userId, setUserId] = useState<number>(0);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState("");
    const [useEffectRequired, setUseEffectRequired] = useState(false);
    const [sterilityObservationList, setSterilityObservationList] = useState([]);
    const [isInitiating, setIsInitiating] = useState(false);
    const [sterilityTestId, setSterilityTestId] = useState("");
    const [observationScheduleId, setObservationScheduleId] = useState("");
    const [showObservationDetails, setShowObservationDetails] = useState(false);
    const [loading, setLoading] = useState(true);
    const [statsData, setStatsData] = useState({
        total_scheduled: 0,
        pending: 0,
        completed: 0,
        due_today: 0,
        overdue: 0,
        growth_detected: 0,
    });
    const [observationStatus, setObservationStatus] = useState("")
    const [recordedBy, setRecordedBy] = useState("");
    const [sterilityTestIdFilter, setSterilityTestIdFilter] = useState("");
    const [fromdate, setFromDate] = useState(
        new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]
    );
    const currentDate = new Date().toISOString().split('T')[0];
    const [toDate, setToDate] = useState(currentDate);
    const [userData , setUserData] = useState<Array<any>>([]);
    const [sterilityTestIdList , setSterilityTestIdList] = useState<Array<any>>([]);
    const [testType, settestType] = useState(0);

    // api call for table sterility observation listing
    const getSterilityObservationList = async (sterilityTestId:string) => {
        try {
            const response = await SterilityTestServices.getSterilityObservationList(page, pageSize, sort, order, sterilityTestId, observationStatus, fromdate, toDate, recordedBy, testType);

            if (response.status && response.statusCode === 200) {
                if (Array.isArray(response.data) && response.data.length > 0) {
                    setTotalPages(response.totalPages ?? 0);
                    setTotalElements(response.totalElements ?? 0);

                    const rowData = response.data.map((element: any) => {

                        const planned = helperFunctions.parseDate(element.scheduled_date_time);
                        const status = (element.schedule_status ?? '').toString().toUpperCase();
                        return {
                            id: element.schedule_id,                          // unique row key
                            testId: element.sterility_test_identifier,        // ST-2026-00004
                            productionSchedule: element.production_schedule_name ?? '-',
                            productName: element.product_name ?? '-',
                            productBatch: element.mt_batch_number ?? '-',     // see note 1
                            testMethod: element.test_method_id === "1" ? "Direct Inoculation" : "Membrane Filtration",      // see note 2
                            tubeCount: element.tube_count ?? 1,               // see note 2
                            observationDay: element.observation_day ? `Day ${element.observation_day}` : '-',
                            plannedDate: helperFunctions.formatDate(element.scheduled_date_time),
                            actualDate: helperFunctions.formatDate(element.observation_date_time),
                            result: toTitleCase(element.observation_results),
                            status: element.schedule_status,         // see note 3
                            canRecord: status === 'PENDING' && planned !== null && planned.getTime() <= now,
                            recordedBy: element.recorded_by_name ?? '-',      // see note 4

                            // extra fields from the API, handy for detail view / actions
                            scheduleId: element.schedule_id,
                            incubationId: element.incubation_id,
                            sterilityTestId: element.sterility_test_id,
                            tubeId: element.tube_id,
                            tubeSerialNumber: element.mt_serial_number,
                            operatorId: element.schedule_operator_id,
                        }
                    });

                    setSterilityObservationList(rowData);
                } else {
                    setSterilityObservationList([]);
                    setTotalPages(0);
                    setTotalElements(0);
                }
            } else {
                setSterilityObservationList([]);
                setTotalPages(0);
                setTotalElements(0);
            }
        } catch (error) {
            console.error('Sterility observation list error', error);
            setSterilityObservationList([]);
        }
    };

    // list operators dropdown
    const getOperatorList = async () => {
        try {
            // const userRole = parseInt(addFilterData.role, 10);  
            const response = await userService.getUserNameList("");
            if (response.status && response.statusCode === 200) {
                try {
                    if (Array.isArray(response.data) && response.data?.length > 0) {
                        // setTotalPages(response?.totalPages)
                        // setTotalElements(response?.totalElements)
                        let rowData: any = response.data.map((element: any, index: number) => {

                            return {
                                slno: index + 1,
                                id: element.id,
                                name: `${element.first_name} ${element.last_name}`,
                            };
                        });
                        setUserData(rowData);
                    }
                } catch (error) {
                    console.log(error);
                }
            } else {
                setUserData([]);
            }
        } catch (error) {
            console.error(error);
        }
    };

    // list sterility test id
    const getSterilityTestId = async () => {
        try {
            const response = await SterilityTestServices.getSterilityTestIds();
            if (response.status && response.statusCode === 200) {
                try {
                    if (Array.isArray(response.data) && response.data?.length > 0) {
                        let rowData: any = response.data.map((element: any, index: number) => {

                            return {
                                slno: index + 1,
                                id: element.id,
                                name: element.sterility_test_identifier,
                            };
                        });
                        setSterilityTestIdList(rowData);
                    }
                } catch (error) {
                    console.log(error);
                }
            } else {
                setSterilityTestIdList([]);
            }
        } catch (error) {
            console.error(error);
        }
    };

    //handle table pagination 
    const handlePageChange = (newPage: any, pageSize?: number) => {
        setPage(newPage);
        if (pageSize) {
        setPageSize(pageSize)
        }
        setUseEffectRequired(true)
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'Completed': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
            case 'Pending': return 'bg-amber-50 text-amber-700 border-amber-100';
            case 'Overdue': return 'bg-red-50 text-red-700 border-red-100';
            default: return 'bg-slate-50 text-slate-700 border-slate-100';
        }
    };

    const getResultStyle = (result: string) => {
        switch (result) {
            case 'Pass': return 'text-emerald-600 font-bold';
            case 'Fail': return 'text-red-600 font-bold';
            case 'Growth Detected': return 'text-rose-600 font-black flex items-center gap-1';
            default: return 'text-slate-400';
        }
    };

    // API call: sterility observation stats
    const getSterilityObservationStat = async () => {
        try {
            setLoading(true);
            const response = await SterilityTestServices.getSterilityObservationStats();

            if (response?.status && response?.statusCode === 200) {
                const data = response.data || {};
                setStatsData({
                    total_scheduled: data.total_scheduled ?? 0,
                    pending: data.pending ?? 0,
                    completed: data.completed ?? 0,
                    due_today: data.due_today ?? 0,
                    overdue: data.overdue ?? 0,
                    growth_detected: data.growth_detected ?? 0,
                });
            } else {
                console.warn('Unexpected response fetching sterility observation stats', response);
            }
        } catch (error) {
            console.error('Error fetching sterility observation stats', error);
        } finally {
            setLoading(false);
        }
    };

    const handleObservationRecord = async (data : any) => {
        try {
                const response = await SterilityTestServices.saveSterilityObservation(data);
                if (response.status && response.statusCode === 200) {
                    setToastType("success")
                    setToastMessage(response.message)
                } else {
                    setToastType("error")
                    setToastMessage(response.message)
                }
                setIsInitiating(false)
                setShowToast(true)
                // addEquipmentModalClose()
                setUseEffectRequired(true)
            } catch (error) {
                console.error('user response', error);
            }
    }

    const toTitleCase = (value: string | null): string => {
        if (!value) return '-';
        return value
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };


    useEffect(() => {
        getSterilityObservationList("");
        getSterilityObservationStat();
        setUseEffectRequired(false);
    }, [useEffectRequired])

    useEffect(() => {
        getSterilityObservationList(sterilityTestIdFilter);
    }, [observationStatus, fromdate, toDate, recordedBy, testType, sterilityTestIdFilter])

    useEffect(() => {
        getOperatorList();
        getSterilityTestId();
    }, [])


    const stats = [
        { label: t('sterilityObservation.stats.totalScheduled'), value: statsData.total_scheduled, color: 'slate', icon: FileText },
        { label: t('sterilityObservation.stats.pending'), value: statsData.pending, color: 'amber', icon: Clock },
        { label: t('sterilityObservation.stats.completed'), value: statsData.completed, color: 'emerald', icon: CheckCircle2 },
        { label: t('sterilityObservation.stats.dueToday'), value: statsData.due_today, color: 'blue', icon: Calendar },
        { label: t('sterilityObservation.stats.overdue'), value: statsData.overdue, color: 'red', icon: AlertTriangle },
        { label: t('sterilityObservation.stats.growthDetected'), value: statsData.growth_detected, color: 'rose', icon: FlaskConical },
    ];


    return (
        <div>
            <HeaderSection />
            <PageHeader pageHeader={t('sterilityObservation.header.title')} pageDescription={t('sterilityObservation.header.subtitle')} />
            <CustomToast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={5000}
                message={toastMessage}
                toastType={toastType}
            />

            {isInitiating && (
                <SterilityObservationRecording
                    onBack={() => setIsInitiating(false)}
                    onSave={(data: any) => handleObservationRecord(data)}
                    sterilityTestId={sterilityTestId}
                    observationScheduleId={observationScheduleId}
                />
            )}

            {showObservationDetails && (
                <SterilityObservationDetails
                    onBack={() => setShowObservationDetails(false)}
                    onViewTest={() => alert("ok view test")}
                    sterilityTestId={sterilityTestId}
                // userName={userName}
                />
            )}

            {!isInitiating && !showObservationDetails && (
                <div className="min-h-screen bg-[#F8FAFC] p-8 pb-20 media-prep-scope">

                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 my-2">
                        {stats.map((stat, idx) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white p-2 ps-3 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col gap-3 group hover:shadow-md transition-all cursor-default"
                            >
                                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-colors
              ${stat.color === 'slate' ? 'bg-slate-50 text-slate-500' : ''}
              ${stat.color === 'amber' ? 'bg-amber-50 text-amber-500' : ''}
              ${stat.color === 'emerald' ? 'bg-emerald-50 text-emerald-500' : ''}
              ${stat.color === 'blue' ? 'bg-blue-50 text-blue-500' : ''}
              ${stat.color === 'red' ? 'bg-red-50 text-red-500' : ''}
              ${stat.color === 'rose' ? 'bg-rose-50 text-rose-500 group-hover:bg-rose-100' : ''}
            `}>
                                    <stat.icon className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-2xl font-black text-slate-800 tracking-tight">{loading ? '–' : stat.value}</p>
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{stat.label}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Main Content Card */}
                    <div className="bg-white rounded-[2.5rem] border border-slate-200/60 shadow-xl shadow-slate-200/20 overflow-hidden my-3">
                        {/* Toolbar & Filters Header */}
                        <div className="p-6 border-b border-slate-100 space-y-6">
                            {/* <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:pb-0 scrollbar-hide">
                                    {['All', 'Due Today', 'Pending', 'Overdue', 'Growth Detected', 'Completed'].map((filter) => (
                                        <button
                                            key={filter}
                                            onClick={() => setActiveQuickFilter(filter)}
                                            className={`px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-full transition-all whitespace-nowrap
                    ${activeQuickFilter === filter
                                                    ? 'bg-[#0F172A] text-white shadow-lg'
                                                    : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                                                }`}
                                        >
                                            {filter}
                                        </button>
                                    ))}
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="relative group">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-orange-500 transition-colors" />
                                        <input
                                            type="text"
                                            placeholder={t('sterilityObservation.toolbar.searchPlaceholder')}
                                            className="pl-10 pr-4 py-2.5 bg-slate-50 border-none rounded-xl text-xs font-medium w-64 focus:ring-2 focus:ring-orange-500/20 transition-all outline-none"
                                        />
                                    </div>
                                    <button
                                        onClick={() => setIsFilterVisible(!isFilterVisible)}
                                        className={`p-2.5 rounded-xl border transition-all ${isFilterVisible ? 'bg-orange-50 border-orange-200 text-orange-600' : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'}`}
                                    >
                                        <Filter className="w-5 h-5" />
                                    </button>
                                </div>
                            </div> */}

                            <div className="d-flex justify-content-end gap-3">
                                {/* <div className="space-y-1.5">
                                    <label className="filterLabelLeft">Test Method</label>
                                    <select
                                        // value={testType}
                                        // onChange={(e) => settestType(Number(e.target.value))}
                                        className="areaTypeSelect"
                                    >
                                        <option value={0} >All Methods</option>
                                        <option value={1} >Direct Inoculation</option>
                                        <option value={2} >Membrane Filtration</option>
                                    </select>
                                </div> */}
                                 <div
                                    className="space-y-1.5"
                                >
                                    <label className="filterLabelLeft">
                                        Sterility Test Id
                                    </label>
                                    <select
                                        className="areaTypeSelect"
                                        name="operatorType"
                                        value={sterilityTestIdFilter}
                                        onChange={(e) => setSterilityTestIdFilter(e.target.value)}
                                    >
                                        <option value="">Select</option>
                                        {sterilityTestIdList?.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div
                                    className="space-y-1.5"
                                >
                                    <label className="filterLabelLeft">
                                        Recorded By
                                    </label>
                                    <select
                                        className="areaTypeSelect"
                                        name="operatorType"
                                        value={recordedBy}
                                        onChange={(e) => setRecordedBy(e.target.value)}
                                    >
                                        <option  value="">Select</option>
                                        {userData?.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="filterLabelLeft">Test Method</label>
                                    <select
                                        value={testType}
                                        onChange={(e) => settestType(Number(e.target.value))}
                                        className="areaTypeSelect"
                                    >
                                        <option value={0} >All Methods</option>
                                        <option value={1} >Direct Inoculation</option>
                                        <option value={2} >Membrane Filtration</option>
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="filterLabelLeft">Status</label>
                                    <select
                                        value={observationStatus}
                                        onChange={(e) => setObservationStatus(e.target.value)}
                                        className="areaTypeSelect"
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="COMPLETED">Completed</option>
                                        <option value="PENDING">Pending</option>
                                        <option value="OVERDUE">Overdue</option>
                                        {/* <option value="4">Media Tube Incubation Completed</option> */}
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="filterLabelLeft">From Date</label>
                                    <input
                                        type="date"
                                        value={fromdate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        className="form-control mt-2"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="filterLabelLeft">To Date</label>
                                    <input
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        className="form-control mt-2"
                                    />
                                </div>
                            </div>

                            <AnimatePresence>
                                {isFilterVisible && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="overflow-hidden"
                                    >
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pb-2">
                                            {[
                                                { label: t('sterilityObservation.filters.testId.label'), placeholder: t('sterilityObservation.filters.testId.placeholder') },
                                                { label: t('sterilityObservation.filters.productBatch.label'), placeholder: t('sterilityObservation.filters.productBatch.placeholder') },
                                                { label: t('sterilityObservation.filters.testMethod.label'), placeholder: t('sterilityObservation.filters.testMethod.placeholder') },
                                                { label: t('sterilityObservation.filters.mediaType.label'), placeholder: t('sterilityObservation.filters.mediaType.placeholder') },
                                                { label: t('sterilityObservation.filters.observationDay.label'), placeholder: t('sterilityObservation.filters.observationDay.placeholder') },
                                                { label: t('sterilityObservation.filters.status.label'), placeholder: t('sterilityObservation.filters.status.placeholder') },
                                                { label: t('sterilityObservation.filters.dateRange.label'), placeholder: t('sterilityObservation.filters.dateRange.placeholder') },
                                                { label: t('sterilityObservation.filters.recordedBy.label'), placeholder: t('sterilityObservation.filters.recordedBy.placeholder') }
                                            ].map((f) => (
                                                <div key={f.label} className="space-y-1.5">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest pl-1">{f.label}</label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            placeholder={f.placeholder}
                                                            className="w-full px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-bold focus:ring-2 focus:ring-orange-500/10 outline-none transition-all placeholder:font-medium placeholder:text-slate-300"
                                                        />
                                                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-300" />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Table Section */}
                        <div className="overflow-x-auto h-[60vh]">
                            <table className="w-full text-center">
                                <thead className="">
                                    <tr className="text-[10px] font-black text-slate-400 border-b border-slate-100 tableHeadRow">
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.sterilityTestId')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.productionSchedule')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.batchCode')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.productName')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.testMethod')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.testTubeCount')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.observationDay')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.observationDate')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.scheduledDate')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.result')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.status')}</th>
                                        <th className="tableHeadData2">{t('sterilityObservation.table.headers.action')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {sterilityObservationList.map((obs: any, idx) => (
                                        <motion.tr
                                            key={obs.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: idx * 0.05 }}
                                            className={`group hover:bg-slate-50/80 transition-colors border-b border-slate-50 ${obs.status === 'Overdue' ? 'bg-red-50/30' : ''}`}
                                        >
                                            <td className="px-6 py-3">
                                                <p className="text-xs font-black text-[#0F172A] tracking-wider">{obs.testId}</p>
                                            </td>
                                            <td className="px-6 py-3">
                                                <p className="text-[10px] font-bold text-slate-500 uppercase">{obs.productionSchedule}</p>
                                            </td>
                                            <td className="px-6 py-3">
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{obs.productBatch}</p>
                                            </td>
                                            <td className="px-6 py-3">
                                                <p className="text-xs font-black text-slate-800">{obs.productName}</p>
                                            </td>
                                            <td className="px-6 py-3">
                                                <div className="flex items-center gap-2">
                                                    <FlaskConical className="w-3 h-3 text-slate-300" />
                                                    <span className="text-[10px] font-bold text-slate-500">{obs.testMethod}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 text-center">
                                                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-100 rounded-md">
                                                    <Beaker className="w-2.5 h-2.5 text-slate-400" />
                                                    <span className="text-[9px] font-black text-slate-600">{obs.tubeCount}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 text-center bg-orange-50/20">
                                                <span className="text-[10px] font-black text-orange-600 uppercase tracking-wider">{obs.observationDay}</span>
                                            </td>
                                            <td className="px-6 py-3 text-center bg-blue-50/10">
                                                <span className="text-[10px] font-black text-orange-600 uppercase tracking-wider">{obs.actualDate}</span>
                                            </td>
                                            <td className="px-6 py-3 text-center bg-blue-50/10">
                                                <button
                                                    onClick={() => setShowObservationDetails(true)}
                                                    className="flex flex-col items-center hover:bg-white p-2 rounded-xl transition-all group/date shadow-sm border border-transparent hover:border-blue-100"
                                                >
                                                    <span className="text-[10px] font-bold text-slate-600 group-hover/date:text-blue-600">{obs.plannedDate}</span>
                                                </button>
                                            </td>
                                            <td className="px-6 py-3">
                                                <div className="space-y-1">
                                                    <div className={getResultStyle(obs.result)}>
                                                        {obs.result === 'Growth Detected' && <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />}
                                                        <span className="text-[11px] uppercase tracking-wider">{obs.result}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3">
                                                <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border transition-all ${getStatusStyle(obs.status)} shadow-sm`}>
                                                    {obs.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 text-right whitespace-nowrap">
                                                <div className="flex items-center justify-end gap-2">
                                                    {obs.canRecord && (
                                                        <button
                                                            onClick={() => {setIsInitiating(true); setSterilityTestId(obs.sterilityTestId); setObservationScheduleId(obs.id) }}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all bg-orange-500 text-white shadow-lg shadow-orange-500/20 hover:bg-orange-600 active:scale-95"
                                                        >
                                                            {t('sterilityObservation.table.record')}
                                                            <ArrowRight className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => {setShowObservationDetails(true); setSterilityTestId(obs.sterilityTestId)}}
                                                        className={`p-2 rounded-lg transition-all
                ${obs.status === 'COMPLETED'
                                                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20 hover:bg-blue-700 active:scale-95'
                                                                : 'bg-slate-50 text-slate-400 hover:bg-slate-200 hover:text-slate-600 border border-slate-100'
                                                            }`}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                    </div>

                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        handlePageChange={handlePageChange}
                    />

                </div>
            )}
        </div>
    );
};

export default SterilityObservation;